/**
 * Threat Intelligence hooks
 */

import { useState, useEffect, useCallback } from 'react';
import { secopsApi } from '../lib/secops-api';

function usePolling<T>(path: string, intervalMs: number, enabled = true) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const result = await secopsApi.get<T>(path);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [path, enabled]);

  useEffect(() => {
    fetch();
    if (intervalMs <= 0 || !enabled) return;
    const timer = setInterval(fetch, intervalMs);
    return () => clearInterval(timer);
  }, [fetch, intervalMs, enabled]);

  return { data, loading, error, refresh: fetch };
}

export function useThreatIntelFeeds() {
  return usePolling<{ data: unknown[] }>('/threat-intel/feeds', 60_000);
}
export function useThreatIntelStats() {
  return usePolling<{ data: Record<string, unknown> }>('/threat-intel/stats', 60_000);
}

export const lookupIOC = (indicator: string) => secopsApi.get<{ data: Record<string, unknown> }>(`/threat-intel/ioc/${encodeURIComponent(indicator)}`);
export const lookupIOCs = (indicators: string[]) => secopsApi.post('/threat-intel?action=lookup', { indicators });
export const enrichEvent = (eventId: string) => secopsApi.post('/threat-intel?action=enrich_event', { event_id: eventId });
export const reportIOC = (data: Record<string, unknown>) => secopsApi.post('/threat-intel?action=report_ioc', data);

// Vuln AI Triage
export function useAITriageResults(filters: Record<string, string> = {}) {
  const qs = new URLSearchParams(filters).toString();
  return usePolling<{ data: unknown[] }>(`/vulns/ai-triage${qs ? `?${qs}` : ''}`, 60_000);
}
export function useAITriageResult(id: string | null) {
  return usePolling<{ data: Record<string, unknown> }>(`/vulns/ai-triage/${id}`, 0, !!id);
}
export const triageSingle = (vulnId: string) => secopsApi.post('/vulns/ai-triage?action=triage_single', { vulnerability_id: vulnId });
export const triageBatch = () => secopsApi.post('/vulns/ai-triage?action=triage_batch');
export const generateRemediationPlan = (filters?: Record<string, string>) => secopsApi.post('/vulns/ai-triage?action=remediation_plan', filters ?? {});

// GRC AI Assistant
export const suggestResponse = (controlId: string, assessmentId: string) => secopsApi.post('/grc/ai-assistant?action=suggest_response', { control_id: controlId, assessment_id: assessmentId });
export const analyzeEvidence = (evidenceId: string) => secopsApi.post('/grc/ai-assistant?action=analyze_evidence', { evidence_id: evidenceId });
export const suggestRemediation = (controlId: string) => secopsApi.post('/grc/ai-assistant?action=suggest_remediation', { control_id: controlId });
export const recommendFrameworks = (profile: Record<string, unknown>) => secopsApi.post('/grc/ai-assistant?action=recommend_frameworks', profile);
export const generateAssessmentSummary = (assessmentId: string) => secopsApi.post('/grc/ai-assistant?action=generate_summary', { assessment_id: assessmentId });
